// Single chat WebSocket per tab, exposed as a small module.
//
// Lifecycle is explicit: callers run connect() on mount and close() on
// unmount; both are idempotent. subscribe(listener) returns an unsubscribe
// function — multiple listeners are supported and each one is responsible
// for filtering frames it cares about (e.g. by session id), so the service
// never needs to know which conversation the UI is showing.

import type { ChatMessageType, SendMessage } from '../types/chat';
import { WS_ENDPOINTS, MAX_RETRIES, RETRY_DELAY_MS } from '@/constants';
import {
  clearMsalCacheAndReload,
  handleAuthenticationError,
} from '@/features/auth/authErrorHandler.ts';

type Listener = (msg: ChatMessageType) => void;
type TokenGetter = () => Promise<string | null>;

let socket: WebSocket | null = null;
let isConnected = false;
let messageQueue: SendMessage[] = [];
const listeners = new Set<Listener>();

let retryCount = 0;
let retryTimeout: number | null = null;
let manuallyClosed = false;

// One-shot guard for the "token expired" reconnect path. Reset to false
// on every successful onopen so a later expiry can trigger one more retry.
let authRetried = false;

let getAuthToken: TokenGetter | null = null;

export function setAuthTokenGetter(getter: TokenGetter): void {
  getAuthToken = getter;
}

function notify(msg: ChatMessageType): void {
  // Snapshot so a listener can unsubscribe during iteration.
  for (const listener of [...listeners]) {
    listener(msg);
  }
}

function flushQueue(): void {
  for (const msg of messageQueue) {
    try {
      socket?.send(JSON.stringify(msg));
    } catch (e) {
      console.error('WebSocket send error (flush):', e);
    }
  }
  messageQueue = [];
}

function scheduleRetry(): void {
  if (retryCount >= MAX_RETRIES) return;
  retryCount += 1;
  if (retryTimeout) clearTimeout(retryTimeout);
  retryTimeout = window.setTimeout(() => {
    void connect();
  }, RETRY_DELAY_MS);
}

async function reconnectWithAuth(): Promise<void> {
  // Tear down without resetting retryCount — the auth-retry guard
  // (authRetried) is what gates this path, not the network retry counter.
  if (socket) {
    socket.close(1000, 'Auth reconnect');
    socket = null;
  }
  isConnected = false;
  manuallyClosed = false;
  await new Promise(resolve => setTimeout(resolve, 100));
  await connect();
}

export async function connect(): Promise<void> {
  if (socket && (isConnected || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  let protocols: string[] | undefined;
  if (getAuthToken) {
    try {
      const token = await getAuthToken();
      if (token) protocols = ['access_token', token];
    } catch (error) {
      console.error('Failed to get auth token for WebSocket:', error);
    }
  }

  socket = protocols
    ? new window.WebSocket(WS_ENDPOINTS.CHAT_STREAM, protocols)
    : new window.WebSocket(WS_ENDPOINTS.CHAT_STREAM);

  socket.onopen = () => {
    isConnected = true;
    retryCount = 0;
    authRetried = false;
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    flushQueue();
  };

  socket.onclose = event => {
    isConnected = false;
    // Do NOT clear messageQueue here — preserve queued messages so they can
    // be flushed after a successful reconnect.
    console.log('WebSocket closed:', {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    });

    // 1008 Policy Violation / 1002 Protocol Error → treated as auth failure.
    if (event.code === 1008 || event.code === 1002) {
      console.error('WebSocket authentication failed:', {
        code: event.code,
        reason: event.reason,
      });

      const isTokenExpired =
        event.reason?.includes('Invalid token') ||
        event.reason?.includes('expired') ||
        event.reason?.includes('Token validation failed');

      if (isTokenExpired && !authRetried && getAuthToken) {
        authRetried = true;
        console.log('Token appears expired, attempting reconnection with fresh token...');
        setTimeout(() => {
          reconnectWithAuth().catch(err =>
            console.error('Failed to reconnect with fresh token:', err)
          );
        }, 1000);
      } else {
        console.error('Non-recoverable authentication error, triggering logout');
        handleAuthenticationError(event);
        notify({
          type: 'error',
          content: `Authentication failed: ${event.reason || 'Invalid credentials'}. Logging out...`,
          source: 'system',
          timestamp: Date.now().toString(),
        } as ChatMessageType);
      }
      return;
    }

    if (event.code === 1000 && event.reason?.toLowerCase().includes('unauthorized')) {
      console.error('WebSocket unauthorized:', event.reason);
      handleAuthenticationError(event);
      return;
    }

    // 1006 Abnormal Closure after multiple retries — likely auth, not network.
    // TODO: refresh token may live in cookies; clearing them has resolved this
    // before. Investigate how the refresh token is stored and used.
    if (event.code === 1006 && !event.wasClean && retryCount >= MAX_RETRIES - 1) {
      console.error('WebSocket failed to connect after multiple attempts - possible auth issue');
      notify({
        type: 'error',
        content:
          'Unable to establish connection. Please check your authentication and try refreshing the page.',
        source: 'system',
        timestamp: Date.now().toString(),
      } as ChatMessageType);
      clearMsalCacheAndReload();
      return;
    }

    if (manuallyClosed) {
      manuallyClosed = false;
      return;
    }

    scheduleRetry();
  };

  socket.onerror = error => {
    console.error('WebSocket error occurred:', error);
    isConnected = false;
  };

  socket.onmessage = event => {
    let data: ChatMessageType;
    try {
      data = JSON.parse(event.data);
    } catch {
      data = event.data as ChatMessageType;
    }
    notify(data);
  };
}

export function close(): void {
  if (socket && (isConnected || socket.readyState === WebSocket.CONNECTING)) {
    // Also close sockets in CONNECTING state — if left alive they fire onopen
    // and flush the message queue into a zombie connection.
    // @see https://www.rfc-editor.org/rfc/rfc6455.html#section-7.4.1
    socket.close(1000, 'Manually closed');
  }
  manuallyClosed = true;
  isConnected = false;
  // Listeners manage their own lifecycle via the unsubscribe returned from
  // subscribe(); don't clear them here.
  messageQueue = [];
  retryCount = 0;
  if (retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = null;
  }
}

export function sendMessage(message: SendMessage): void {
  if (
    !socket ||
    socket.readyState === WebSocket.CLOSING ||
    socket.readyState === WebSocket.CLOSED ||
    !isConnected
  ) {
    void connect();
    messageQueue.push(message);
    return;
  }
  try {
    socket.send(JSON.stringify(message));
  } catch (e) {
    console.error('WebSocket send error:', e);
    notify({
      type: 'error',
      content: 'WebSocket send error',
      source: 'system',
    } as ChatMessageType);
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
